import { requestCallCenter, unwrapEnvelopeValue } from "./CallCenterApi";

const withOptionalNumber = (value) =>
  value === "" || value === null || typeof value === "undefined"
    ? undefined
    : Number(value);

/**
 * @typedef {Object} TicketListFilters
 * @property {number} [page]
 * @property {number} [size]
 * @property {string} [accountNo]
 * @property {string} [accountId]
 * @property {boolean} [called]
 * @property {Array<number|string>|string} [statusIds]
 * @property {number|string} [minOutstanding]
 * @property {number|string} [maxOutstanding]
 * @property {string} [fromPaymentDate] yyyy-MM-dd
 * @property {string} [toPaymentDate] yyyy-MM-dd
 * @property {"OUTSTANDING_ASC"|"OUTSTANDING_DESC"|"PAYMENT_DATE_ASC"|"PAYMENT_DATE_DESC"|"CALLED_AT_ASC"|"CALLED_AT_DESC"|"UPDATED_DESC"} [sortBy]
 * @property {number|string} [assigneeId]
 */

/**
 * @typedef {Object} TicketListPage
 * @property {Array<Object>} items
 * @property {number} totalElements
 * @property {number} totalPages
 * @property {number} page
 * @property {number} size
 * @property {boolean} hasNext
 * @property {boolean} hasPrevious
 */

export const createTicket = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to create ticket");
};

export const createBulkTickets = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to create bulk tickets");
};

export const uploadBulkTickets = async ({
  file,
  priority,
  stopOnError = false,
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("priority", priority);
  formData.append("stopOnError", String(Boolean(stopOnError)));
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/bulk/upload",
    data: formData,
  });
  return unwrapEnvelopeValue(envelope, "Failed to upload bulk ticket file");
};

export const bulkSoftDeleteTickets = async ({ ticketIds }) => {
  const envelope = await requestCallCenter({
    method: "delete",
    url: "tickets/bulk",
    data: { ticketIds },
  });
  return unwrapEnvelopeValue(envelope, "Failed to bulk delete tickets");
};

export const listTickets = async ({ accountNo, assigned } = {}) => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets",
    params: {
      ...(accountNo ? { accountNo } : {}),
      ...(assigned === true || assigned === false ? { assigned } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to list tickets");
};

/**
 * @param {TicketListFilters} filters
 * @returns {Promise<TicketListPage|Array<Object>>}
 */
export const listTicketsV2 = async (filters = {}) => {
  const statusIdsParam = Array.isArray(filters.statusIds)
    ? filters.statusIds.filter(Boolean).join(",")
    : filters.statusIds;
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets",
    params: {
      ...(filters.accountNo ? { accountNo: filters.accountNo } : {}),
      ...(filters.accountId ? { accountId: filters.accountId } : {}),
      ...(filters.called === true || filters.called === false
        ? { called: filters.called }
        : {}),
      ...(statusIdsParam ? { statusIds: statusIdsParam } : {}),
      ...(typeof filters.minOutstanding !== "undefined"
        ? { minOutstanding: withOptionalNumber(filters.minOutstanding) }
        : {}),
      ...(typeof filters.maxOutstanding !== "undefined"
        ? { maxOutstanding: withOptionalNumber(filters.maxOutstanding) }
        : {}),
      ...(filters.fromPaymentDate ? { fromPaymentDate: filters.fromPaymentDate } : {}),
      ...(filters.toPaymentDate ? { toPaymentDate: filters.toPaymentDate } : {}),
      ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
      ...(typeof filters.assigneeId !== "undefined" && filters.assigneeId !== ""
        ? { assigneeId: withOptionalNumber(filters.assigneeId) }
        : {}),
      ...(typeof filters.page === "number" ? { page: filters.page } : {}),
      ...(typeof filters.size === "number" ? { size: filters.size } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to list tickets");
};

/**
 * @param {{
 *   id: string,
 *   status?: string,
 *   remarksComment?: string,
 *   additionalComments?: string,
 *   targetOutstanding?: number|string,
 *   targetPaymentDate?: string,
 *   calledAt?: string,
 * }} payload
 */
export const updateTicketStatus = async ({
  id,
  status,
  remarksComment,
  additionalComments,
  targetOutstanding,
  targetPaymentDate,
  calledAt,
}) => {
  const envelope = await requestCallCenter({
    method: "patch",
    url: `tickets/${id}/status`,
    data: {
      ...(status ? { status } : {}),
      ...(remarksComment ? { remarksComment } : {}),
      ...(additionalComments ? { additionalComments } : {}),
      ...(typeof targetOutstanding !== "undefined" && targetOutstanding !== ""
        ? { targetOutstanding: Number(targetOutstanding) }
        : {}),
      ...(targetPaymentDate ? { targetPaymentDate } : {}),
      ...(calledAt ? { calledAt } : {}),
    },
  });
  return unwrapEnvelopeValue(envelope, "Failed to update ticket status");
};

export const updateTicketStatusSelf = async ({ id, status }) => {
  const envelope = await requestCallCenter({
    method: "patch",
    url: `tickets/${id}/status/self`,
    data: { status },
  });
  return unwrapEnvelopeValue(envelope, "Failed to update own ticket status");
};

export const createTicketStatus = async (payload) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: "tickets/statuses",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to save ticket status");
};

export const listTicketStatuses = async () => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "tickets/statuses",
  });
  return unwrapEnvelopeValue(envelope, "Failed to load ticket statuses");
};

export const refreshTicketOutstanding = async ({ id }) => {
  const envelope = await requestCallCenter({
    method: "post",
    url: `tickets/${id}/outstanding/refresh`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to refresh outstanding");
};

export const getTeamProgress = async () => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "callcenter/analytics/team-progress",
  });
  return unwrapEnvelopeValue(envelope, "Failed to load team progress");
};
